import {
  compose,
  withPropsOnChange,
  withState,
  withHandlers,
  mapProps,
  pure,
} from 'recompose';
import { getTime, toDate, startOfDay, endOfDay } from 'date-fns';
import * as R from 'ramda';

import { withDevice } from '~/features/App/Device';

import { Search as SC } from './Search';

const setDefaultState = ({ defaultValues = {} }) => ({
  isVisible: false,
  calendarData: {},
  values: { ...defaultValues },
});

const getDateTimeFromString = R.pipe(
  parseInt,
  toDate,
);

const getDateFromElement = endDay =>
  R.pipe(
    R.pathOr(new Date().getTime(), ['dataset', 'value']),
    getDateTimeFromString,
    R.cond([[() => !!endDay, endOfDay], [R.T, startOfDay]]),
    getTime,
  );

const enhance = compose(
  withState('state', 'setState', setDefaultState),
  withPropsOnChange(
    ['defaultValues'],
    ({ state, setState, tabs, defaultValues }) => {
      if (R.isEmpty(tabs)) {
        setState(setDefaultState({ defaultValues }));

        return {};
      }

      const { values } = state;
      const isTabSwitched =
        !!values.searchType && values.searchType !== defaultValues.searchType;

      if (isTabSwitched) {
        setState({
          ...state,
          isVisible: true,
          calendarData: {},
          values: {
            ...values,
            searchType: defaultValues.searchType,
          },
        });
      } else {
        const { validate } = R.find(R.propEq('active', true), tabs);
        const isValid = validate ? validate(defaultValues) : true;

        setState({
          ...state,
          isVisible: !isValid,
          calendarData: {},
          values: { ...defaultValues },
        });
      }

      return {};
    },
  ),
  withHandlers({
    onShow: ({ state, setState }) => e => {
      e && e.preventDefault();

      setState({ ...state, isVisible: true });
    },
    onHide: ({ state, setState, tabs, defaultValues, onSubmit }) => e => {
      e && e.preventDefault();

      if (R.isEmpty(tabs)) {
        setState({
          ...state,
          isVisible: false,
          calendarData: {},
          values: { ...defaultValues },
        });

        return;
      }

      const { values } = state;
      const activeTab = R.find(R.propEq('active', true), tabs);
      const isValid = activeTab.validate ? activeTab.validate(values) : true;

      if (!isValid) {
        onSubmit();
      }

      setState({
        ...state,
        isVisible: false,
        calendarData: {},
        values: isValid ? { ...defaultValues } : {},
      });
    },
  }),
  withHandlers({
    onFocus: ({ state, setState }) => e => {
      e && e.preventDefault();

      const name = R.path(['target', 'name'], e);
      const { direction, type, value } = R.pathOr(
        { type: 'numeric' },
        ['target', 'dataset'],
        e,
      );

      const calendarData =
        type === 'date'
          ? {
              key: `${name}.${direction}`,
              value: getDateTimeFromString(value),
              ...(direction === '0'
                ? {
                    maxDate: R.pipe(
                      getDateFromElement(true),
                      toDate,
                    )(document.getElementById(`${name}.1`)),
                  }
                : {
                    minDate: R.pipe(
                      getDateFromElement(),
                      toDate,
                    )(document.getElementById(`${name}.0`)),
                  }),
            }
          : {};

      setState({ ...state, calendarData });
    },
    onChange: ({ state, setState, onSubmit }) => (
      composedName,
      type,
      value,
    ) => {
      const isSearch = R.equals(type, 'search');
      const values = { ...state.values };

      if (isSearch && R.isEmpty(value) && !state.isVisible) {
        delete values.search;

        onSubmit(values);

        return;
      }

      const isDate = R.equals(type, 'date');
      const isCalendar = R.equals(type, 'calendar');
      const [name, direction] = R.split(/[^\w\d]+/, composedName);

      if (R.isNil(direction)) {
        values[name] = value;
      } else {
        let from = document.getElementById(`${name}.0`).value;
        let to = document.getElementById(`${name}.1`).value;

        if (isDate || isCalendar) {
          from = getDateFromElement()(document.getElementById(`${name}.0`));
          to = getDateFromElement(true)(document.getElementById(`${name}.1`));
        }

        values[name] = [from, to];
        values[name][direction] = value;
      }

      let calendarData = {};

      if (!isCalendar) {
        calendarData = isDate
          ? { key: composedName, value: toDate(value) }
          : state.calendarData;
      }

      setState({ ...state, values, calendarData });
    },
    onClear: ({ setState, onSubmit }) => () => {
      setState(setDefaultState({}));
      onSubmit();
    },
    onSubmit: ({
      tabs,
      state,
      setState,
      onHide,
      onSubmit,
      onRefetch,
    }) => () => {
      const { values } = state;

      if (R.isEmpty(tabs)) {
        onSubmit(values);

        return;
      }

      const isEmptyValues = R.isEmpty(R.keys(R.omit(['searchType'], values)));

      if (isEmptyValues) {
        onHide();
        onRefetch();

        return;
      }

      setState(setDefaultState({}));
      onSubmit(values);
    },
  }),
  mapProps(({ onRefetch, defaultValues, state, setState, ...rest }) => ({
    ...rest,
    ...state,
  })),
  withDevice,
  pure,
);

export const Search = enhance(SC);

Search.defaultProps = {
  onSubmit: () => {},
};

Search.Filter = SC.Filter;

Search.defaultProps = {
  tabs: [],
  onRefetch: () => {},
};
